// The diagnostics a feedback report carries. Two pieces:
//
//   * a small ring buffer of uncaught errors, so "it broke" arrives with the stack that
//     broke it. Installed once by the feedback launcher; window-level only — console.* is
//     left alone so nothing the app logs is intercepted or reshaped.
//   * snapshot(): the browser facts that make a bug reproducible (agent, viewport, locale,
//     timezone, app version) plus the page the reporter was on.
//
// Nothing here is sent anywhere on its own: it is read once, when someone files a report.

const MAX_ERRORS = 20;
const buffer: string[] = [];
let installed = false;

function push(line: string): void {
  buffer.push(line.slice(0, 1000));
  if (buffer.length > MAX_ERRORS) buffer.shift();
}

/** Start recording uncaught errors and rejected promises. Idempotent. */
export function installErrorRecorder(): () => void {
  if (installed || typeof window === 'undefined') return () => undefined;
  installed = true;
  const onError = (e: ErrorEvent) => push(`${e.message}${e.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : ''}`);
  const onRejection = (e: PromiseRejectionEvent) => {
    const r = e.reason as unknown;
    push(`Unhandled rejection: ${r instanceof Error ? `${r.name}: ${r.message}` : String(r)}`);
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    installed = false;
  };
}

/** The recorded errors, oldest first. */
export function recentErrors(): string[] {
  return [...buffer];
}

export function clearRecentErrors(): void {
  buffer.length = 0;
}

export interface Diagnostics {
  user_agent?: string;
  platform?: string;
  language?: string;
  timezone?: string;
  viewport?: string;
  screen?: string;
  device_pixel_ratio?: number;
  app_version?: string;
  url?: string;
  referrer?: string;
  console_errors?: string[];
}

/** Everything the browser will tell us for free, captured at the moment of reporting. */
export function snapshot(): Diagnostics {
  if (typeof window === 'undefined') return {};
  const version = (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? undefined;
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  const out: Diagnostics = {
    user_agent: navigator.userAgent,
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    device_pixel_ratio: window.devicePixelRatio,
    url: window.location.href,
  };
  if (timezone) out.timezone = timezone;
  if (version) out.app_version = version;
  if (document.referrer) out.referrer = document.referrer;
  const errors = recentErrors();
  if (errors.length) out.console_errors = errors;
  return out;
}
